import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'ps_customer_token';
const USER_KEY = 'ps_customer_data';

const CustomerAuthContext = createContext(null);

export const useCustomerAuth = () => {
  const context = useContext(CustomerAuthContext);
  if (!context) {
    throw new Error('useCustomerAuth must be used within CustomerAuthProvider');
  }
  return context;
};

export const CustomerAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginCallback, setLoginCallback] = useState(null);

  // Load user from localStorage on mount
  useEffect(() => {
    const loadUser = async () => {
      const token = localStorage.getItem(TOKEN_KEY);
      const savedUser = localStorage.getItem(USER_KEY);

      if (token && savedUser) {
        try {
          // Validate token by calling /me
          const res = await fetch(`${API_URL}/api/public/auth/me`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (res.ok) {
            const data = await res.json();
            setUser(data);
            localStorage.setItem(USER_KEY, JSON.stringify(data));
          } else {
            // Token expired/invalid
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(USER_KEY);
          }
        } catch {
          // Network error, use cached data
          try {
            setUser(JSON.parse(savedUser));
          } catch {
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(USER_KEY);
          }
        }
      }
      setLoading(false);
    };
    loadUser();
  }, []);

  const sendOTP = useCallback(async (phone) => {
    const res = await fetch(`${API_URL}/api/public/auth/send-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Failed to send OTP');
    return data;
  }, []);

  const verifyOTP = useCallback(async (phone, otp) => {
    const res = await fetch(`${API_URL}/api/public/auth/verify-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone, otp })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Verification failed');

    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.customer));
    setUser(data.customer);

    return data;
  }, []);

  const verifyMSG91Token = useCallback(async (phone, token) => {
    const res = await fetch(`${API_URL}/api/public/auth/verify-msg91-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone, token })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Verification failed');

    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.customer));
    setUser(data.customer);

    return data;
  }, []);

  const completeProfile = useCallback(async (profileData) => {
    const token = localStorage.getItem(TOKEN_KEY);
    const res = await fetch(`${API_URL}/api/public/auth/complete-profile`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(profileData)
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Failed to save profile');

    if (data.token) localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.customer));
    setUser(data.customer);
    return data;
  }, []);

  const updateProfile = useCallback(async (updates) => {
    const token = localStorage.getItem(TOKEN_KEY);
    const res = await fetch(`${API_URL}/api/public/auth/profile`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(updates)
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Failed to update profile');

    localStorage.setItem(USER_KEY, JSON.stringify(data));
    setUser(prev => ({ ...prev, ...data }));
    return data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
  }, []);

  const getToken = useCallback(() => localStorage.getItem(TOKEN_KEY), []);

  // Trigger login modal with a callback for after-login action
  const requireAuth = useCallback((callback) => {
    if (user) {
      callback(user);
      return;
    }
    setLoginCallback(() => callback);
    setShowLoginModal(true);
  }, [user]);

  const onLoginSuccess = useCallback((userData) => {
    setUser(userData);
    setShowLoginModal(false);
    if (loginCallback) {
      loginCallback(userData);
      setLoginCallback(null);
    }
  }, [loginCallback]);

  const closeLoginModal = useCallback(() => {
    setShowLoginModal(false);
    setLoginCallback(null);
  }, []);

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    sendOTP,
    verifyOTP,
    verifyMSG91Token,
    completeProfile,
    updateProfile,
    logout,
    getToken,
    requireAuth,
    showLoginModal,
    onLoginSuccess,
    closeLoginModal,
    setShowLoginModal
  };

  return (
    <CustomerAuthContext.Provider value={value}>
      {children}
    </CustomerAuthContext.Provider>
  );
};
